// src/features/Auth/useAuthModal.js

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import { useAuth } from "../../AuthUser";
import API from "../../api";
import AuthModal from "../Auth/AuthModal";

const AuthModalContext = createContext(null);

export function AuthModalProvider({ children }) {
  const auth = useAuth();
  const [visible, setVisible] = useState(false);

  const afterSuccessRef = useRef(null);
  const [justAuthedAt, setJustAuthedAt] = useState(0);

  // NEW: whether modal can be closed manually (X, backdrop, ESC)
  const closableRef = useRef(false);

  // OPEN
  // openAuthModal({ closable: true|false, onSuccess: fn })
  const openAuthModal = useCallback(
      (opts = {}) => {
        const now = Date.now();
        if (justAuthedAt && now - justAuthedAt < 2000) return;

        afterSuccessRef.current =
            typeof opts.onSuccess === "function" ? opts.onSuccess : null;

        closableRef.current = opts.closable === true;

        setVisible(true);
      },
      [justAuthedAt]
  );

  // CLOSE
  const closeAuthModal = useCallback(() => {
    setVisible(false);
  }, []);

  // LOGIN SUCCESS
  const handleSuccess = useCallback(async () => {
    try {
      const me = await API.get("/accounts/me").catch(() => null);
      if (me?.data) auth.setUser?.(me.data);
      setJustAuthedAt(Date.now());
    } finally {
      closeAuthModal();
      afterSuccessRef.current?.();
      afterSuccessRef.current = null;
    }
  }, [auth, closeAuthModal]);

  // BACK BUTTON closes modal if open, navigation proceeds normally
  useEffect(() => {
    const onPopState = () => {
      if (visible) setVisible(false);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [visible]);

  const value = useMemo(
      () => ({
        openAuthModal,
        closeAuthModal,
        isAuthModalOpen: visible,
      }),
      [openAuthModal, closeAuthModal, visible]
  );

  return (
      <AuthModalContext.Provider value={value}>
        {children}
        <AuthModal
            show={visible}
            onHide={closableRef.current ? closeAuthModal : undefined}
            onSuccess={handleSuccess}
            closable={closableRef.current}
        />
      </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) throw new Error("useAuthModal must be used within <AuthModalProvider>");
  return ctx;
}
